import { type MasterCategoryType } from '../../../types/masterSearchTypes';
import { MASTER_REFERENCE_CONFIG } from '../../../data/masterReferenceConfig';
import {formatBidTimestamp} from '../../../utils/date'

// 親コンポーネントから受け取るデータの定義
type Props = {
  category: MasterCategoryType; // 表示対象マスタ
  data: any[];                  // 表示するデータの中身（リスト）
  isLoading: boolean;           // データ取得中かどうか
};

/**
 * マスタデータの検索結果をテーブル表示するコンポーネント
 * 設定ファイルに基づいて、列の数やヘッダー名を動的に切り替えます。
 */
export const MasterDataTable = ({ category, data, isLoading }: Props) => {
  
  // カテゴリに応じた設定（列定義など）を取り出す
  // 例: categoryが 'seller' なら、売手用の列設定（売手コード、売手名）が取得される
  const config = MASTER_REFERENCE_CONFIG[category];

  // 1. ローディング中の表示
  // データがまだ来ていないときは、テーブルではなく「データを読み込み中...」を表示してガードする
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-48 bg-white rounded-lg border border-gray-200">
        <div className="text-gray-500">データを読み込み中...</div>
      </div>
    );
  }

  // 2. データ表示（メイン部分）
  /**
   * 高さ制御のロジック:
   * TABLE_MIN_H: ヘッダ(約40px) + ボディ(約52px*5) + フッタ(約40px) + 枠線 ≒ 350px
   * EXTERNAL_H: 上部ヘッダ(約100px) + タイトル(約60px) + 検索フォーム(約120px) + 外枠の余白(約40px)の概算。
   */
  const TABLE_MIN_H_5_ROWS = '350px';
  const EXTERNAL_ELEMENTS_H = '320px';
  const hasMinRows = data.length >= 5;

  return (
    <div 
      className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col overflow-hidden"
      style={{
        // データが5行以上の時のみ、最低5行分の高さを確保（少ない時は中身に合わせる）
        minHeight: hasMinRows ? TABLE_MIN_H_5_ROWS : 'auto',
        // 画面の高さから外側要素分を引いた値を上限にし、それを超えたらスクロール
        maxHeight: `calc(100vh - ${EXTERNAL_ELEMENTS_H})`,
      }}
    >
      {/* overflow-auto: 横幅が狭い時や、maxHeightに達した時にスクロールさせる */}
      <div className="overflow-auto flex-1">
        <table className="min-w-full divide-y divide-gray-200 border-separate border-spacing-0">
          
          {/* --- ヘッダ部分 (thead) --- */}
          <thead className="bg-gray-50">
            <tr>
              {/* 設定ファイル(config.columns) に定義された列の数だけループして見出しを作る */}
              {config.columns.map((col, index) => (
                <th 
                  key={index}
                  scope="col"
                  // sticky top-0: スクロール時にヘッダを固定
                  // width設定があれば適用し、なければ自動幅にする
                  className={`sticky top-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200 ${col.width || ''}`}
                >
                  {col.header} {/* 例: "売手コード", "売手名" など */}
                </th>
              ))}
            </tr>
          </thead>

          {/* --- ボディ部分 (tbody) --- */}
          <tbody className="bg-white divide-y divide-gray-200">
            {/* データが1件以上ある場合 */}
            {data.length > 0 ? (
              // データの数だけ行を作成
              data.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-blue-50 transition-colors">
                  
                  {/* さらに、列設定の数だけセルを作成 */}
                  {/* これにより、データの中に余計な項目があっても、設定にある列だけを表示できる */}
                  {config.columns.map((col, colIndex) => (
                    <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {/*動的なデータアクセス
                         row はデータ1行分（オブジェクト）。
                         col.accessor は取り出したいプロパティ名（文字列）。
                         例: row["sellerName"] のようにして値を取り出す。
                      */}
                      {(() => {
                        const value = row[col.accessor];

                        switch (col.type) {
                          case 'checkbox':
                            return (
                              <div className="flex">
                                <input
                                  type="checkbox"
                                  checked={!!value}// nullやundefined対策で二重否定
                                  readOnly
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                              </div>
                            );
                          case 'date':
                            return typeof value === 'string'
                              ? formatBidTimestamp(value).replace(/-/g, '/').replace('T', ' ').substring(0, 19)// "2026-01-27T08:59:45.813" -> "2026/01/27 08:59:45" に変換
                              : value;
                          default:
                            return value;
                        }
                    })()}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              // データが0件の場合
              <tr>
                {/* colSpanを使って、全列を結合した1つのセルにする */}
                <td colSpan={config.columns.length} className="px-6 py-12 text-center text-gray-400 text-sm">
                  データがありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* --- フッタ部分 --- */}
      {/* flex-shrink-0: 高さを固定。border-t: テーブルとの境界線。
          行末に追従するようにスクロールコンテナの外に配置。
      */}
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 text-sm text-gray-500 flex justify-between items-center flex-shrink-0">
        <span>全 {data.length} 件を表示</span>
      </div>
    </div>
  );
};